import {
  APIDocumentArchiveFolder,
  ArdoqId,
  DocumentArchiveRenamePayload,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import {
  deleteEntity,
  fetchJson,
  headers,
  Method,
  toRequestOptions,
} from './api';
import { resultsToPayloadOrError } from '@ardoq/common-helpers';

const getOrgUrl = (orgId: ArdoqId): string => `/api/folder/org/${orgId}/folder`;

const getItemFromOrgUrl = (orgId: ArdoqId, itemId: ArdoqId): string =>
  `${getOrgUrl(orgId)}/${itemId}`;

const getWorkspaceUrl = (workspaceId: ArdoqId): string =>
  `/api/folder/workspace/${workspaceId}/folder`;

const getItemFromWorkspaceUrl = (orgId: ArdoqId, itemId: ArdoqId): string =>
  `${getWorkspaceUrl(orgId)}/${itemId}`;

const fetchWorkspaceFolders = async (
  workspaceId: ArdoqId
): ApiResponse<APIDocumentArchiveFolder[]> => {
  const request = new Request(`/api/folder/workspace/${workspaceId}/folder`, {
    headers,
  });
  return fetchJson<APIDocumentArchiveFolder[]>(request);
};

const fetchOrgFolders = async (
  orgId: ArdoqId
): ApiResponse<APIDocumentArchiveFolder[]> => {
  const request = new Request(`/api/folder/org/${orgId}/folder`, {
    headers,
  });
  return fetchJson<APIDocumentArchiveFolder[]>(request);
};

const rename = ({
  type,
  typeId,
  renameRequest,
}: DocumentArchiveRenamePayload) => {
  const request = new Request(
    `/api/folder/${type}/${typeId}/folder/${renameRequest._id}`,
    toRequestOptions(Method.PATCH, renameRequest)
  );
  return fetchJson<APIDocumentArchiveFolder>(request);
};

const moveMultipleToFolder = async ({
  type,
  typeId,
  foldersWithNewParent,
}: {
  type: string;
  typeId: ArdoqId;
  foldersWithNewParent: APIDocumentArchiveFolder[];
}): ApiResponse<APIDocumentArchiveFolder[]> => {
  const requests = foldersWithNewParent.map(folder => {
    return moveFolderToFolder(type, typeId, folder);
  });
  const responses = await Promise.all(requests);
  return resultsToPayloadOrError(responses);
};

const moveFolderToFolder = (
  type: string,
  typeId: ArdoqId,
  folderWithNewParent: APIDocumentArchiveFolder
): ApiResponse<APIDocumentArchiveFolder> => {
  const request = new Request(
    `/api/folder/${type}/${typeId}/folder/${folderWithNewParent._id}`,
    toRequestOptions(Method.PUT, folderWithNewParent)
  );
  return fetchJson<APIDocumentArchiveFolder>(request);
};

const create = async ({
  name,
  type,
  typeId,
}: {
  name: string;
  type: string;
  typeId: ArdoqId;
}): ApiResponse<APIDocumentArchiveFolder> => {
  const request = new Request(
    `/api/folder/${type}/${typeId}/folder`,
    toRequestOptions(Method.POST, {
      name,
      description: '',
    })
  );
  return fetchJson<APIDocumentArchiveFolder>(request);
};

const deleteMultiple = async (ids: ArdoqId[]): ApiResponse<ArdoqId[]> => {
  const requests = ids.map(id => folderApi.delete(id));
  const responses = await Promise.all(requests);
  return resultsToPayloadOrError(responses);
};

export const folderApi = {
  fetchWorkspaceFolders,
  fetchOrgFolders,
  getOrgUrl,
  getItemFromOrgUrl,
  getWorkspaceUrl,
  getItemFromWorkspaceUrl,
  rename,
  delete: deleteEntity('folder'),
  deleteMultiple,
  moveMultipleToFolder,
  create,
};
